/**
 * GovIntegracoes — WhatsApp Z-API + sistemas municipais.
 * Reusa a mesma lógica do privado (Operacao > Integracoes) mas com
 * texto institucional e badges GOV.
 *
 * v0.99.9 · Wave 2 · Fev/2026
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  WhatsappLogo, CheckCircle, XCircle, ArrowClockwise, LinkSimple,
  Buildings, FileText, Envelope,
} from "@phosphor-icons/react";

export default function GovIntegracoes() {
  const [zapi, setZapi] = useState({ instance_id: "", token: "", connected: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [checked, setChecked] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tenants/settings");
      setZapi({
        instance_id: data?.zapi_instance_id || "",
        token: data?.zapi_token ? "••••••••" : "",
        connected: !!data?.zapi_connected,
      });
      setChecked(true);
    } catch {
      setChecked(true);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenants/settings", {
        zapi_instance_id: zapi.instance_id,
        zapi_token: zapi.token && !zapi.token.startsWith("••") ? zapi.token : undefined,
      });
      toast.success("Conexão salva");
      load();
    } catch {
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/zapi/test");
      if (data?.ok) toast.success("Conexão OK · Z-API respondeu");
      else toast.error(data?.message || "Falha no teste");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no teste da Z-API");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div data-testid="gov-integracoes-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700/70 font-medium mb-1">
          Configurações
        </div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
          <WhatsappLogo size={22} className="text-emerald-700" weight="regular" />
          WhatsApp & Integrações
        </h1>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          Conecte o número oficial do órgão e integre com sistemas municipais.
        </p>
      </div>

      {/* WhatsApp Z-API */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 mb-4" data-testid="integracao-whatsapp">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-200 grid place-items-center">
              <WhatsappLogo size={20} className="text-emerald-700" weight="fill" />
            </div>
            <div>
              <div className="font-heading font-semibold text-slate-900 text-[15px]">WhatsApp Business (Z-API)</div>
              <div className="text-[11.5px] text-slate-500 mt-0.5">Número oficial do órgão · canal certificado</div>
            </div>
          </div>
          <div>
            {zapi.connected ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-medium">
                <CheckCircle size={12} weight="fill" /> Conectado
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-slate-600 text-[11px] font-medium border border-slate-200">
                <XCircle size={12} /> Não conectado
              </span>
            )}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[11px] font-medium text-slate-700 mb-1 block">Instance ID</label>
            <input
              value={zapi.instance_id}
              onChange={(e) => setZapi({ ...zapi, instance_id: e.target.value })}
              data-testid="zapi-instance"
              placeholder="ex.: 3E1B0A1A2C3D4E5F"
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px] bg-white focus:border-emerald-600 focus:outline-none font-mono"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-700 mb-1 block">Token</label>
            <input
              type="password"
              value={zapi.token}
              onChange={(e) => setZapi({ ...zapi, token: e.target.value })}
              data-testid="zapi-token"
              placeholder="•••••••••••••••"
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px] bg-white focus:border-emerald-600 focus:outline-none font-mono"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={save}
            disabled={saving}
            data-testid="zapi-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white text-[13px] font-medium transition-colors disabled:opacity-60"
          >
            {saving ? "Salvando…" : "Salvar conexão"}
          </button>
          <button
            onClick={testConnection}
            disabled={testing}
            data-testid="zapi-test"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-[13px] text-slate-700 disabled:opacity-60"
          >
            <ArrowClockwise size={12} />
            {testing ? "Testando…" : "Testar conexão"}
          </button>
          <a
            href="https://z-api.io"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] text-emerald-700 hover:text-emerald-900"
          >
            <LinkSimple size={12} /> Obter Instance & Token
          </a>
        </div>
      </section>

      {/* Sistemas municipais — coming soon */}
      <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="integracao-sistemas">
        <div className="flex items-center gap-2 mb-3">
          <Buildings size={18} className="text-emerald-700" />
          <div className="font-heading font-semibold text-slate-900 text-[15px]">Sistemas Municipais</div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <IntegrationCard icon={FileText} title="e-SIC" desc="Protocolos e transparência ativa" />
          <IntegrationCard icon={Envelope} title="ERP Municipal" desc="Betha, IPM, GEO, Fiorilli, etc." />
          <IntegrationCard icon={Buildings} title="Secretarias" desc="Roteamento inteligente por assunto" />
        </div>
      </section>
    </div>
  );
}

function IntegrationCard({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={16} className="text-slate-500" />
        <div className="font-medium text-slate-900 text-[13px]">{title}</div>
      </div>
      <div className="text-[11px] text-slate-500 leading-relaxed">{desc}</div>
      <div className="mt-2 inline-block text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">
        Sob demanda
      </div>
    </div>
  );
}
